/**
 * Business-error model for the Onboarding flow.
 *
 * Discriminated union: the generic `network` / `api` transport failures (from
 * the app-wide `@/utils/infra-error` classifier) plus the form-specific
 * `validation` variant (field-level messages produced by `validateDraft`).
 *
 * Like Reset Password, the server message IS surfaced to the user (e.g.
 * "Invitation expired"); the caller already holds a one-time token, so there
 * is no account information left to enumerate.
 */
import { classifyInfraError, type InfraFailure } from '@/utils/infra-error';

export type OnboardingError =
  | InfraFailure
  | { kind: 'validation'; fields: Record<string, string> };

/**
 * Map an infrastructure error into a domain `OnboardingError`. Only ever
 * yields a `network` / `api` failure; `validation` errors are produced
 * directly by the feature's `validateDraft`.
 */
export function toOnboardingError(e: unknown): OnboardingError {
  return classifyInfraError(e, 'An error occurred.');
}

/**
 * User-facing message for a failed on-mount invitation validation. Keeps the
 * original page's fallback copy when the backend gave no message of its own.
 */
export function toInvitationValidationMessage(e: unknown): string {
  return classifyInfraError(e, 'Invalid or expired invitation link.').message;
}
